import React from "react";
import { useTheme } from "@/context/ThemeContext";

/* ============================================================
   KD9 OFFICIAL LOGO SYSTEM
   - Uses the official KAHOS DELTA 9 master logos (PNG).
   - kd9-master.png      → Dark variant (gold on dark backdrop)
   - kd9-master-light.png → Light variant (gold + black "KHAOS DELTA 9" on white)
   The active variant follows the global theme automatically.
   ============================================================ */

const KD9_LOGO_DARK = "/logos/kd9-master.png";
const KD9_LOGO_LIGHT = "/logos/kd9-master-light.png";

const useLogoSrc = () => {
  const { theme } = useTheme();
  return theme === "dark" ? KD9_LOGO_DARK : KD9_LOGO_LIGHT;
};

/**
 * KD9LogoCircle — Circular version (master logo as-is).
 * Use for: Loading, splash, hero, dashboard, watermark contexts, empty states.
 */
export const KD9LogoCircle = ({
  size = 120,
  glow = true,
  className = "",
  alt = "KAHOS DELTA 9",
}) => {
  const src = useLogoSrc();
  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size }}
      data-testid="kd9-logo-circle"
    >
      {glow && (
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, var(--kd9-glow) 0%, transparent 65%)",
            filter: "blur(8px)",
          }}
          aria-hidden="true"
        />
      )}
      <img
        src={src}
        alt={alt}
        className="kd9-logo-img relative z-10"
        draggable={false}
        style={glow ? undefined : { filter: "none" }}
      />
    </div>
  );
};

/**
 * KD9LogoHorizontal — Horizontal lockup.
 * Use for: Navbar, footer, headers, institutional sections.
 */
export const KD9LogoHorizontal = ({
  size = 56,
  height,
  className = "",
  withTagline = false,
  alt = "KAHOS DELTA 9",
}) => {
  const src = useLogoSrc();
  const h = height ?? size;
  return (
    <div
      className={`flex items-center gap-3 ${className}`}
      data-testid="kd9-logo-horizontal"
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          height: h * 1.6,
          width: "auto",
          objectFit: "contain",
          filter: "drop-shadow(0 0 10px var(--kd9-glow))",
        }}
      />
      {withTagline && (
        <span
          className="font-cormorant italic hidden md:inline"
          style={{
            color: "var(--kd9-text-soft)",
            fontSize: Math.max(12, h * 0.28),
            lineHeight: 1.2,
            maxWidth: 220,
          }}
        >
          La energía que despierta lo ancestral
        </span>
      )}
    </div>
  );
};

/**
 * KD9LogoCompact — Small reduced version.
 * Use for: Sidebar collapsed, mobile, avatar placeholders, favicon, notifications.
 */
export const KD9LogoCompact = ({
  size = 36,
  className = "",
  alt = "KD9",
}) => {
  const src = useLogoSrc();
  return (
    <div
      className={`relative inline-block rounded-full overflow-hidden ${className}`}
      style={{ width: size, height: size, background: "transparent" }}
      data-testid="kd9-logo-compact"
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          width: size * 1.6,
          height: size * 1.6,
          marginLeft: -size * 0.3,
          marginTop: -size * 0.15,
          objectFit: "cover",
          objectPosition: "center 35%",
          filter: "drop-shadow(0 0 8px var(--kd9-glow))",
        }}
      />
    </div>
  );
};

/**
 * KD9Watermark — Large faint logo behind content.
 */
export const KD9Watermark = ({
  size = 600,
  className = "",
  opacity,
}) => {
  const src = useLogoSrc();
  return (
    <div
      className={`kd9-watermark absolute pointer-events-none select-none ${className}`}
      style={{
        width: size,
        height: size,
        opacity: opacity ?? undefined,
      }}
      aria-hidden="true"
      data-testid="kd9-watermark"
    >
      <img
        src={src}
        alt=""
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
      />
    </div>
  );
};

export default KD9LogoHorizontal;
